/**
 * Reactive Component Module
 * 
 * This module provides the base class for reactive web components.
 * It implements the IReactiveComponent interface and provides functionality
 * for subscribing to store changes and managing event listeners.
 * 
 * Components that extend this class can:
 * - Subscribe to store properties and react to changes
 * - Register and manage event listeners that persist across renders
 * - Automatically clean up subscriptions and event listeners
 */

import { getDefaultStore, getStore } from "../../store/store-registry.ts";
import { devlog } from "../../logic/utils.ts";
import type { IReactiveComponent, IReactiveStore, SubbedProps, UpdateHandler, EventBinding, EventBindingMap } from "../../as-components.d.ts";

/**
 * Base class for reactive web components
 * 
 * This class provides functionality for creating web components that can:
 * - Subscribe to store properties and react to changes
 * - Register and manage event listeners that persist across renders
 * - Automatically clean up subscriptions and event listeners
 * 
 * Components that extend this class should:
 * - Call subscribeTo() to subscribe to store properties
 * - Implement an update handler to react to store changes
 * - Use registerEventListener() or registerEventListeners() to manage events
 */
export class AsReactiveComponent extends HTMLElement implements IReactiveComponent {  
  private storeRef: IReactiveStore;
  // Track which properties this component is subscribed to
  private subscribedProps: string[] = [];
  private updateHandler:UpdateHandler = ()=>{};
  private throttleTime = 10;
  private renderTimer:number|null = null;
  protected eventBindings: EventBinding[] = [];
  
  /**
   * Create a new reactive component
   * 
   * @param storeName Optional name of the store to use. Defaults to 'default'.
   * @throws Error if the specified store is not found
   */
  constructor(storeName: string = 'default'){
    super();
    // Get the store with the specified name
    const store = getStore(storeName);
    if (!store) {
      throw new Error(`Store "${storeName}" not found`);
    }
    this.storeRef = store;
  }

  /**
   * Switch to a different store
   * 
   * This method allows switching to a different store after initialization.
   * It will unsubscribe from the current store and subscribe to the new one
   * with the same properties.
   * 
   * @param storeName Name of the store to switch to
   * @throws Error if the specified store is not found
   */
  useStore(storeName: string): void {
    // Unsubscribe from current store
    this.unsubscribe();
    
    // Get the new store
    const newStore = getStore(storeName);
    if (!newStore) {
      throw new Error(`Store "${storeName}" not found`);
    }
    
    // Update store reference
    this.storeRef = newStore;
    
    // Resubscribe to the same properties
    if (this.subscribedProps.length > 0) {
      this.storeRef.subscribe(this, this.subscribedProps);
      this.renderWithCurrentValues();
    }
  }

  /**
   * Subscribe to store properties and set up rendering
   * 
   * This method subscribes the component to the specified store properties
   * and sets up the update handler that will be called when those properties
   * change.
   * 
   * @param props Array of property names to subscribe to
   * @param updateHandler Optional function to call when properties change
   */
  subscribeTo(props:string[], updateHandler?:UpdateHandler){
    this.subscribedProps = props;
    // Register with the store but don't duplicate the data
    this.storeRef.subscribe(this, props);
    this.updateHandler = updateHandler ?? (()=>{});
    
    // Initial render with current values
    this.renderWithCurrentValues();
  }

  /**
   * Unsubscribe from all store properties
   * 
   * This method removes all store subscriptions for this component.
   */
  unsubscribe(){
    this.storeRef.unsubscribe(this);
  }

  /**
   * Add additional subscriptions to existing ones
   * 
   * This method adds new property subscriptions without removing existing ones.
   * It triggers a re-render with the updated values.
   * 
   * @param props Additional property names to subscribe to
   */
  addSubscriptions(props:string[]){
    // Add new props to our tracking list
    this.subscribedProps = [...new Set([...this.subscribedProps, ...props])];
    // Register with the store
    this.storeRef.subscribe(this, props);
    
    // Re-render with updated values
    this.renderWithCurrentValues();
  }

  /**
   * Called by the store when a subscribed property changes
   * 
   * This method is called by the store when any of the subscribed properties
   * change. It throttles the rendering to avoid excessive updates.
   * 
   * @param props Object containing the changed properties
   */
  updateSubbedValues(props:{[propKey:string]:any}): void {
    // devlog(this,"store updated:",props);
    
    // Throttle rendering to avoid excessive updates
    if(this.renderTimer){
      clearTimeout(this.renderTimer);
    }
    this.renderTimer = setTimeout(()=>{
      this.renderWithCurrentValues();
    }, this.throttleTime) as unknown as number;
  }
  
  /**
   * Register a single event listener
   * 
   * This method registers an event listener that will be maintained across
   * renders. The listener will be automatically attached and detached during
   * renders.
   * 
   * @param selector CSS selector for the target element
   * @param event Event name to listen for
   * @param handler Event handler function
   */
  registerEventListener(
    selector: string,
    event: string,
    handler: (e: Event) => void
  ): void {
    this.eventBindings.push({ selector, event, handler });
  }

  /**
   * Register multiple event listeners at once
   * 
   * This method provides a convenient way to register multiple event listeners
   * using a dictionary format.
   * 
   * @param bindingsMap Dictionary of selectors and their event handlers
   * 
   * Example:
   * registerEventListeners({
   *   '#signInLink': {
   *     'click': () => console.log('clicked'),
   *     'mouseover': () => console.log('hovered')
   *   },
   *   '#signOutLink': {
   *     'click': () => console.log('signed out')
   *   }
   * });
   */
  registerEventListeners(bindingsMap: EventBindingMap): void {
    Object.entries(bindingsMap).forEach(([selector, events]) => {
      Object.entries(events).forEach(([event, handler]) => {
        this.registerEventListener(selector, event, handler);
      });
    });
  }

  /**
   * Attach all registered event listeners
   * 
   * This private method attaches all registered event listeners to their
   * target elements in the shadow DOM.
   */
  private attachEventListeners(): void {
    this.eventBindings.forEach(({ selector, event, handler }) => {
      const elements = this.shadowRoot?.querySelectorAll(selector);
      elements?.forEach(element => {
        element.addEventListener(event, handler);
      });
    });
  }

  /**
   * Remove all registered event listeners
   * 
   * This private method removes all registered event listeners from their
   * target elements in the shadow DOM.
   */
  private removeEventListeners(): void {
    this.eventBindings.forEach(({ selector, event, handler }) => {
      const elements = this.shadowRoot?.querySelectorAll(selector);
      elements?.forEach(element => {
        element.removeEventListener(event, handler);
      });
    });
  }

  /**
   * Render the component with current values
   * 
   * This private method handles the rendering process:
   * 1. Removes existing event listeners
   * 2. Gets current values from the store
   * 3. Calls the update handler with the current values
   * 4. Reattaches event listeners
   */
  private renderWithCurrentValues(): void {
    // Remove existing event listeners before render
    this.removeEventListeners();
    
    // Get current values directly from the store
    const currentValues = this.subscribedProps.reduce((acc, prop) => {
      acc[prop] = this.storeRef[prop];
      return acc;
    }, {} as SubbedProps);
    
    // devlog(this, "rendering component with", currentValues);
    this.updateHandler(currentValues);
    
    // Reattach event listeners after render
    this.attachEventListeners();
  }
  
  /**
   * Get current values of subscribed properties
   * 
   * This helper method returns an object containing the current values
   * of all subscribed properties.
   * 
   * @returns Object with current values of subscribed properties
   */
  getSubscribedValues(): SubbedProps {
    return this.subscribedProps.reduce((acc, prop) => {
      acc[prop] = this.storeRef[prop];
      return acc;
    }, {} as SubbedProps);
  }

  /**
   * Clean up when the component is removed
   * 
   * This lifecycle method is called when the component is removed from
   * the DOM. It removes all event listeners and store subscriptions.
   */
  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();
  }
}
